import type { CaseData, CaseInfo, DailySchedule, GameConfig } from '../types';
import { buildDataUrl, DATA_FILES } from '../utils/environment';
import { analytics } from './analyticsService';

const fetchJson = async <T>(url: string): Promise<T> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const loadCaseData = async (
  caseId: string,
): Promise<CaseData | null> => {
  try {
    const url = buildDataUrl(DATA_FILES.CASE_DATA, { caseId });
    const data = await fetchJson<CaseData>(url);

    return data;
  } catch (error) {
    console.error('Error loading case data:', error);

    analytics.trackException(
      error,
      'Error loading case data',
      'DataService',
      'loadCaseData',
    );
    return null;
  }
};

export const getAllCaseInfos = async (): Promise<CaseInfo[]> => {
  try {
    const url = buildDataUrl(DATA_FILES.CASES_INDEX);
    const data = await fetchJson<{ cases: CaseInfo[] }>(url);

    if (!data.cases) {
      return [];
    }

    return data.cases;
  } catch (error) {
    console.error('Error fetching case list:', error);
    analytics.trackException(
      error,
      'Error fetching case list',
      'DataService',
      'getAllCaseInfos',
    );
    return [];
  }
};

export const loadGameConfig = async (): Promise<GameConfig | null> => {
  try {
    const url = buildDataUrl(DATA_FILES.GAME_CONFIG);
    const data = await fetchJson<GameConfig>(url);

    return data;
  } catch (error) {
    console.error('Error loading game config:', error);
    analytics.trackException(
      error,
      'Error loading game config',
      'DataService',
      'loadGameConfig',
    );
    return null;
  }
};

export const loadCaseVisibility = async (): Promise<string[] | null> => {
  try {
    const url = buildDataUrl(DATA_FILES.CASE_VISIBILITY);
    const data = await fetchJson<{ visibleCaseIds: string[] }>(url);

    return data.visibleCaseIds || null;
  } catch (error) {
    console.error('Error loading case visibility:', error);
    analytics.trackException(
      error,
      'Error loading case visibility',
      'DataService',
      'loadCaseVisibility',
    );
    return null;
  }
};

export const loadDailySchedule = async (): Promise<DailySchedule | null> => {
  try {
    const url = buildDataUrl(DATA_FILES.DAILY_SCHEDULE);
    return await fetchJson<DailySchedule>(url);
  } catch (error) {
    console.error('Error loading daily schedule:', error);
    analytics.trackException(
      error,
      'Error loading daily schedule',
      'DataService',
      'loadDailySchedule',
    );
    return null;
  }
};
